import { WarningCircle } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import ProfileImage from "common/components/ui/ProfileImage";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

interface WorkerAccountsTagProps {
  size: "sm" | "md";
  owners: string[];
  isUsingNote: boolean;
}

const workerAccountsTagVariants = cva(
  "inline-flex items-center gap-x-1 rounded-full py-0.5 pl-0.5",
  {
    variants: {
      variant: {
        default:
          "bg-purple-50 text-black-950 [&_.worker-account-image]:ring-purple-50",
        warning:
          "bg-yellow-100 text-black-950 [&_.warning-icon]:fill-yellow-500 [&_.worker-account-image]:ring-yellow-100",
        disabled:
          "bg-black-100 text-black-400 [&_.worker-account-image]:ring-black-100",
      },
      size: {
        sm: "text-button-xxs pr-2",
        md: "text-button-xs pr-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export default function WorkerAccountsTag({
  size,
  owners,
  isUsingNote,
}: WorkerAccountsTagProps) {
  const { accounts } = useSelectedWorkspaceContext();

  const isMulti = owners.length > 1;
  const workerAccounts = accounts.filter(({ id }) => owners.includes(id));

  const invalidWorkerAccounts = workerAccounts.filter(
    ({ state, license, premium, type }) => {
      const isMissingPremium = isUsingNote && !premium;
      const isMissingLicense = type === "user" && !license;
      const isLoggedOut = state !== "LoggedIn";

      return isMissingPremium || isMissingLicense || isLoggedOut;
    },
  );

  let tagVariant: VariantProps<typeof workerAccountsTagVariants>["variant"];

  if (
    invalidWorkerAccounts.length === workerAccounts.length ||
    !workerAccounts.length
  ) {
    tagVariant = "disabled";
  } else if (invalidWorkerAccounts.length) {
    tagVariant = "warning";
  }

  const hasWarning = !!tagVariant;

  let label = "No accounts";

  if (isMulti) {
    label = `${owners.length} accounts`;
  } else if (workerAccounts.length) {
    label = workerAccounts[0].full_name;
  }

  return (
    <Tooltip>
      <TooltipTrigger
        className={workerAccountsTagVariants({ variant: tagVariant, size })}
      >
        {workerAccounts.length ? (
          <div className="flex shrink-0 flex-row -space-x-1.5 rtl:space-x-reverse">
            {workerAccounts
              .slice(0, 3)
              .map(({ full_name, id, picture_url: pictureUrl }) => (
                <ProfileImage
                  key={id}
                  size={size === "md" ? "2xs" : "3xs"}
                  className={clsx(isMulti && "worker-account-image ring-1")}
                  src={pictureUrl}
                  alt={full_name}
                />
              ))}
          </div>
        ) : (
          <ProfileImage size={size === "md" ? "2xs" : "3xs"} src="" />
        )}

        <span className="line-clamp-1 break-all">{label}</span>

        <RenderIf condition={hasWarning}>
          <WarningCircle
            weight="fill"
            size={16}
            className="warning-icon -mr-1"
          />
        </RenderIf>
      </TooltipTrigger>
      <TooltipContent>
        {workerAccounts.length ? (
          <div className="flex flex-col gap-y-1 max-h-64 overflow-y-auto scrollbar-thin">
            {workerAccounts.map(
              ({ id, full_name, state, license, type, premium }) => {
                let label = "OK";

                if (type === "user" && !license) {
                  label = "No seat";
                } else if (state === "LoggedOut") {
                  label = "Disconnected";
                } else if (isUsingNote && !premium) {
                  label = "No premium";
                }

                return (
                  <span key={id}>
                    {full_name}: {label}
                  </span>
                );
              },
            )}
          </div>
        ) : (
          "There are no sender accounts in this campaign"
        )}
      </TooltipContent>
    </Tooltip>
  );
}
